import React from 'react';
import Select, { components } from "react-select";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';

const STORE_TYPE_LABELS = {
    MAIN: 'Central',
    BRANCH: 'Branch',
    COST_CENTER: 'Department',
};

// Builds a flat option list from the stores array, keeping parent stores
// followed immediately by their child_stores (if any), so the existing
// react-select behaviour (single flat list, search, keyboard nav) is preserved.
const buildStoreOptions = (stores = []) => {
    const options = [];
    stores.forEach((store) => {
        options.push({
            label: store.name,
            value: store.id,
            storeType: store.store_type,
            isPmsConnected: !!store.is_pms_connected,
            isChild: false,
        });
        if (Array.isArray(store.child_stores) && store.child_stores.length > 0) {
            store.child_stores.forEach((child) => {
                options.push({
                    label: child.name,
                    value: child.id,
                    storeType: child.store_type,
                    isPmsConnected: !!child.is_pms_connected,
                    isChild: true,
                    parentName: store.name,
                });
            });
        }
    });
    return options;
};

const StoreOption = (props) => {
    const { data } = props;
    const showTooltip = data.label && data.label.length > 25;
    const nameContent = (
        <span className="store-switch-option-name">
            {data.isChild ? <span className="store-switch-child-arrow">&#8627;</span> : null}
            {data.label}
        </span>
    );
    return (
        <components.Option {...props}>
            <div className={`store-switch-option${data.isChild ? ' store-switch-option-child' : ''}`}>
                {showTooltip ? (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{data.label}</Tooltip>}>
                        {nameContent}
                    </OverlayTrigger>
                ) : nameContent}
                <span className="store-switch-option-badges">
                    {data.storeType ? (
                        <span className={`store-type-badge store-type-${String(data.storeType).toLowerCase()}`}>
                            {STORE_TYPE_LABELS[data.storeType] || data.storeType}
                        </span>
                    ) : null}
                    {data.isPmsConnected ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>PMS Connected</Tooltip>}>
                            <span className="store-pms-badge">
                                <FontAwesomeIcon icon={faLink} />
                            </span>
                        </OverlayTrigger>
                    ) : null}
                </span>
            </div>
        </components.Option>
    );
};

const StoreSingleValue = (props) => {
    const { data } = props;
    const innerProps = { ...props.innerProps };
    if (innerProps.title) {
        delete innerProps.title;
    }
    return (
        <components.SingleValue {...props} innerProps={innerProps}>
            <span className="store-switch-selected-value">
                {data?.label}
                {data?.isPmsConnected ? (
                    <FontAwesomeIcon icon={faLink} className="store-pms-badge-inline" title="PMS Connected" />
                ) : null}
            </span>
        </components.SingleValue>
    );
};

const Menu = (props) => {
    return (
        <>
            <components.Menu {...props}>
                <div className="dropdowninnersection">
                    <div className="dropdownlabelbtn">
                        <div className="dropheaderlabel">{props.selectProps.menuHeader}</div>
                        <button className="dropheaderbtn" onClick={props.selectProps.oncloseMenu}>
                            <img src={require('../../Utility/Public/images/dropcloseicon.png')} />
                        </button>
                    </div>
                    <div>{props.children}</div>
                </div>
            </components.Menu>
        </>
    );
};

// Drop-in replacement for AutosuggestComponent used specifically for the
// Switch Store dropdown. Accepts the raw `stores` array (with optional
// `child_stores`) and `currentStore`, and behaves exactly like the previous
// AutosuggestComponent from the consumer's point of view (same handleOnChange
// signature: receives { label, value, ... }).
const StoreSwitcherSelect = (props) => {
    const {
        stores,
        currentStore,
        handleOnChange,
        placeholder,
        isDisabled,
        isSearchable,
        closeButton,
        menuHeader,
        classNamePrefix,
        name,
        id,
        menuPosition,
    } = props;

    const options = buildStoreOptions(stores);
    const selectedValue = options.find((opt) => Number(opt.value) === Number(currentStore?.id)) || (
        currentStore ? { label: currentStore.name, value: currentStore.id } : null
    );

    let selectRef = null;
    const oncloseMenu = () => {
        if (selectRef) selectRef.blur();
    };

    if (closeButton === false) {
        return (
            <div>
                <Select
                    value={selectedValue}
                    isClearable={false}
                    isSearchable={isSearchable}
                    options={options}
                    name={name}
                    isMulti={false}
                    onChange={handleOnChange}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    className="basic-select"
                    id={id}
                    menuPosition={menuPosition}
                    classNamePrefix={classNamePrefix}
                    components={{ Option: StoreOption, SingleValue: StoreSingleValue }}
                />
            </div>
        );
    }

    return (
        <div>
            <Select
                value={selectedValue}
                isClearable={false}
                isSearchable={isSearchable}
                options={options}
                name={name}
                isMulti={false}
                onChange={handleOnChange}
                placeholder={placeholder}
                isDisabled={isDisabled}
                className="basic-select"
                components={{ Menu, Option: StoreOption, SingleValue: StoreSingleValue }}
                menuHeader={menuHeader}
                oncloseMenu={oncloseMenu}
                ref={(scope) => { selectRef = scope; }}
                classNamePrefix={classNamePrefix}
            />
        </div>
    );
};

StoreSwitcherSelect.defaultProps = {
    isSearchable: true,
    closeButton: false,
    menuHeader: '',
    classNamePrefix: 'common-dropdown-box',
    placeholder: 'Choose store...',
    isDisabled: false,
    id: '',
    menuPosition: '',
};

export default StoreSwitcherSelect;
